import React, { useRef, useState } from 'react';
import cn from 'classnames';
import { useDrag, useDrop } from 'react-dnd';
import { useTrip } from '../../contexts/trip-context';
import { ItemCardHeader } from './item-card-header';
import { ItemCardMedia } from './item-card-media';
import { ItemCardContent } from './item-card-content';
import { ItemCardActions } from './item-card-actions';
import { ItemCardFooter } from './item-card-footer';
import { ItemCardScheduleInfo } from './item-card-schedule-info';
import styles from './base-item-card.module.css';
import { BaseItemCardProps } from './types';

export const BaseItemCard: React.FC<BaseItemCardProps> = ({
  id,
  title,
  subtitle,
  description,
  websiteUrl,
  activityType,
  icon,
  iconColor,
  photos,
  thumbnailUrl,
  displayState,
  videoId,
  autoPlayVideoToken,
  mediaPlaybackSessionToken,
  mediaPlaybackOwner,
  onEditDisplayClick,
  selectable = false,
  isSelected: externalIsSelected,
  onSelectionChange,
  primaryAction,
  secondaryActions,
  optionsMenu,
  onMessageClick,
  onDeleteClick,
  onEditClick,
  onAddClick,
  onBookTourClick,
  badges,
  links,
  metadata,
  commentMetadata,
  draggable = false,
  dragType = 'item-card',
  hoverable = true,
  onHoverStart,
  onHoverEnd,
  onDragStart,
  onDragEnd,
  onClick,
  layout = 'standard',
  orientation = 'vertical',
  minWidth = '400px',
  maxWidth = '450px',
  className,
  style,
  borderColor,
  highlightOnHover = true,
  mapped = true,
  unmappedLabel,
  addButtonLoading = false,
  addButtonDisabled = false,
  showContent = false,
  hidePhotos = false,
  showFooter = true,
  showWebsiteUrl = true,
  showActivityType = true,
  showCheckbox,
  showMapIcon = true,
  showSourceButton = true,
  showMessageButton = false,
  showDeleteButton = false,
  showEditButton = false,
  showAddButton = false,
  showOptionsMenu = true,
  index,
  moveActivity,
  findActivity,
  itineraries,
  onDayChange,
  showScheduleInfo = false,
  researchSection
}) => {
  // console.log('[BaseItemCard] RENDERED - id:', id, 'title:', title);

  // Use local state for selection instead of context
  const [localSelected, setLocalSelected] = useState(false);

  // TripContext integration
  const tripContext = useTrip();
  const { setHoveredActivity, sourceActivityId, setSourceActivityId } =
    tripContext || {};

  // Determine if item is selected (from external prop or local state)
  const isSelected =
    externalIsSelected !== undefined ? externalIsSelected : localSelected;

  const ref = useRef<HTMLDivElement | null>(null);

  // Drop setup for reordering (only if moveActivity and findActivity are provided)
  const [{ handlerId }, drop] = useDrop(
    () => ({
      accept: dragType,
      collect: (monitor) => ({
        handlerId: monitor.getHandlerId()
      }),
      hover(item: any, monitor) {
        if (
          !ref.current ||
          !moveActivity ||
          !findActivity ||
          index === undefined
        )
          return;

        const dragIndex = findActivity(item.id).index;
        const hoverIndex = index;

        // Don't replace items with themselves
        if (dragIndex === hoverIndex) return;

        // Only reorder if we found the activity (internal reordering)
        if (dragIndex !== -1 && dragIndex !== hoverIndex) {
          const hoverBoundingRect = ref.current.getBoundingClientRect();
          const hoverMiddleY =
            (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
          const clientOffset = monitor.getClientOffset();
          if (!clientOffset) return;
          const hoverClientY = clientOffset.y - hoverBoundingRect.top;

          // Only perform the move when the mouse has crossed half of the items height
          // When dragging downwards, only move when the cursor is below 50%
          // When dragging upwards, only move when the cursor is above 50%
          if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
          if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

          moveActivity(dragIndex, hoverIndex);
        }
      }
    }),
    [id, index, moveActivity, findActivity, dragType]
  );

  // Drag and drop setup
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: dragType,
      item: () => {
        onDragStart?.();
        return { id };
      },
      end: () => {
        onDragEnd?.();
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      }),
      canDrag: draggable
    }),
    [id, draggable, dragType, onDragStart, onDragEnd]
  );

  // Selection handler
  const handleSelectionChange = (checked: boolean) => {
    if (onSelectionChange) {
      onSelectionChange(id, checked);
    } else {
      // Phase 2: Use local state as fallback
      setLocalSelected(checked);
    }
  };

  // Hover handlers
  const handleMouseEnter = () => {
    if (hoverable) {
      onHoverStart?.(id);
      setHoveredActivity?.(id.toString());
    }
  };

  const handleMouseLeave = () => {
    if (hoverable) {
      onHoverEnd?.(id);
      setHoveredActivity?.(null);
    }
  };

  // Click handler
  const handleClick = () => {
    onClick?.(id);
  };

  // Combine refs for drag and drop using callback ref
  const combinedRef = (node: HTMLDivElement | null) => {
    // Update the mutable ref
    (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;

    if (node) {
      if (draggable && moveActivity && findActivity) {
        drag(drop(node));
      } else if (draggable) {
        drag(node);
      }
    }
  };

  return (
    <div
      ref={combinedRef}
      data-handler-id={handlerId}
      className={cn(
        styles.baseItemCard,
        styles[layout],
        styles[orientation],
        {
          [styles.dragging]: isDragging,
          [styles.selected]: isSelected,
          [styles.hoverable]: hoverable && highlightOnHover,
          [styles.clickable]: !!onClick
        },
        className
      )}
      style={{
        ...style,
        minWidth: minWidth,
        maxWidth: maxWidth,
        borderColor: borderColor,
        opacity: isDragging ? 0.8 : 1,
        cursor: isDragging
          ? 'grabbing'
          : draggable
            ? 'grab'
            : onClick
              ? 'pointer'
              : 'default',
        display: 'flex',
        flexDirection: 'column'
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      data-item-id={id}
    >
      <ItemCardHeader
        id={id}
        title={title}
        subtitle={subtitle}
        selectable={selectable}
        isSelected={isSelected}
        onSelectionChange={handleSelectionChange}
        icon={icon}
        iconColor={iconColor}
        isSource={sourceActivityId === id.toString()}
        onSourceChange={setSourceActivityId}
        optionsMenu={optionsMenu}
        onMessageClick={onMessageClick}
        onDeleteClick={onDeleteClick}
        onEditClick={onEditClick}
        commentMetadata={commentMetadata}
        isDragging={isDragging}
        mapped={mapped}
        unmappedLabel={unmappedLabel}
        showCheckbox={showCheckbox}
        showMapIcon={showMapIcon}
        showSourceButton={showSourceButton}
        showMessageButton={showMessageButton}
        showDeleteButton={showDeleteButton}
        showEditButton={showEditButton}
        showAddButton={showAddButton}
        showOptionsMenu={showOptionsMenu}
        onAddClick={onAddClick}
        addButtonLoading={addButtonLoading}
        addButtonDisabled={addButtonDisabled}
      />

      {showScheduleInfo && (
        <ItemCardScheduleInfo
          activityId={id.toString()}
          itineraries={itineraries || []}
          onDayChange={onDayChange}
        />
      )}

      {/* {(() => {
        // console.log('[BaseItemCard] showScheduleInfo:', showScheduleInfo);
        // console.log('[BaseItemCard] itineraries:', itineraries);
        // console.log(
        //   '[BaseItemCard] itineraries.length:',
        //   itineraries?.length || 0
        // );
        // console.log('[BaseItemCard] Condition result:', showScheduleInfo);
        // return null;
      })()} */}

      {!hidePhotos && (
        <ItemCardMedia
          photos={photos}
          thumbnailUrl={thumbnailUrl}
          placeName={title}
          layout={layout}
          displayState={displayState}
          videoId={videoId}
          autoPlayVideoToken={autoPlayVideoToken}
          mediaPlaybackSessionToken={mediaPlaybackSessionToken}
          mediaPlaybackOwner={mediaPlaybackOwner}
          onEditDisplayClick={onEditDisplayClick}
        />
      )}

      {showContent && (
        <ItemCardContent
          description={description}
          badges={badges}
          metadata={metadata}
          layout={layout}
        />
      )}

      <div>
        {(() => {
          // Use websiteUrl prop directly if showWebsiteUrl is enabled
          const footerWebsiteUrl = showWebsiteUrl ? websiteUrl : undefined;
          const footerActivityType = showActivityType
            ? activityType || (badges?.[0]?.text as string) || undefined
            : undefined;

          return (
            showFooter && (
              <ItemCardFooter
                websiteUrl={footerWebsiteUrl}
                activityType={footerActivityType}
                onBookTourClick={
                  onBookTourClick ? () => onBookTourClick(id) : undefined
                }
              />
            )
          );
        })()}
        {researchSection}
        <ItemCardActions
          primaryAction={primaryAction}
          secondaryActions={secondaryActions}
          itemId={id}
        />
      </div>
    </div>
  );
};

// Memoize to prevent unnecessary re-renders
export default React.memo(BaseItemCard);
